import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';
import { useAuthStore } from '@/stores/authStore';
import { dripService } from '@/services/dripService';
import useAlertStore from '@/stores/alertStore';
import { noteGoApiFetch } from '@/services/noteGoApi/noteGoApiFetcher';
import { errorReporter } from '@/services/errorReportingService';
import { logger } from '@/services/loggerService';
import { getCheckoutPrice } from '@/utils/checkoutPrice';

interface VerifyCheckoutResponse {
  status: string;
  plan_name: string | null;
  product_id: string | null;
  subscription_status?: string;
  mode?: string;
}

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { currentUser, fetchAndSetUserData, userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const [isUpdatingDrip, setIsUpdatingDrip] = useState(true);
  const [displayPlanName, setDisplayPlanName] = useState<string>('your new plan');
  const [isSubscriptionSynced, setIsSubscriptionSynced] = useState(false);
  const [purchasedProductId, setPurchasedProductId] = useState<string | null>(null);
  // Recorded by the plan pages when they opened this session. Verification only
  // returns the product, and a monthly → annual switch keeps the same product.
  const purchasedPriceId = useMemo(() => (sessionId ? getCheckoutPrice(sessionId) : null), [sessionId]);
  const [syncFailed, setSyncFailed] = useState(false);

  // Use refs to prevent infinite loops and manage polling state
  const syncRetryCountRef = useRef(0);
  const isPollingRef = useRef(false);

  // Check if subscription is in a valid state for navigation
  // Must match statuses that will pass route guards (ACTIVE or TRIAL in accountStatus.ts)
  const isValidSubscriptionStatus = useCallback((status: string | null | undefined): boolean => {
    if (!status) return false;
    // These statuses map to ACTIVE or TRIAL in accountStatus.ts
    const validStatuses = ['active', 'free', 'trialing', 'payment_failed', 'incomplete'];
    return validStatuses.includes(status);
  }, []);

  // A status string alone is NOT proof the purchase synced. A free user already
  // has status 'active' BEFORE paying, so a status-only check passes before the
  // webhook has run — which showed a success screen to users whose upgrade never
  // landed, and made the SUBSCRIPTION_SYNC_TIMEOUT alert below unreachable.
  // When we know which product was bought, require the doc to actually reflect it.
  const isSyncedToPurchase = useCallback(
    (
      subscription: { status?: string | null; product_id?: string | null; price_id?: string | null } | null | undefined,
    ) => {
      if (!isValidSubscriptionStatus(subscription?.status)) return false;
      // Fail CLOSED when a checkout happened but we could not determine which
      // product was bought (verification errored or returned no product_id).
      // Returning true here would reinstate the exact status-only check this fix
      // exists to remove. Only the no-session_id flow may fall back to status.
      if (!purchasedProductId) return !sessionId;
      if (subscription?.product_id !== purchasedProductId) return false;
      // A same-tier monthly → annual switch matches on product while the doc still
      // carries the monthly price, so require the exact price when this tab knows it.
      return !purchasedPriceId || subscription?.price_id === purchasedPriceId;
    },
    [isValidSubscriptionStatus, purchasedProductId, purchasedPriceId, sessionId],
  );

  useEffect(() => {
    // Refresh user data on mount to sync with webhook updates
    fetchAndSetUserData({ force: true });
  }, [fetchAndSetUserData]);

  // Check as soon as verification has told us which product was purchased.
  // Gated on isUpdatingDrip so we never mark synced before purchasedProductId is
  // known — that would fall back to the status-only check and pass trivially.
  useEffect(() => {
    if (isUpdatingDrip) return;
    if (isSyncedToPurchase(userData?.subscription)) {
      setIsSubscriptionSynced(true);
    }
  }, [isUpdatingDrip, userData?.subscription, isSyncedToPurchase]);

  // Poll for subscription sync after checkout verification completes
  useEffect(() => {
    // Only start polling if verification is done, subscription not synced, and not already polling
    if (isUpdatingDrip || isSubscriptionSynced || isPollingRef.current) {
      return;
    }

    const maxSyncRetries = 15; // 30 seconds max (15 * 2s)
    let syncTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    isPollingRef.current = true;

    const checkSubscriptionSync = async () => {
      // Re-fetch user data to check for updates
      try {
        await fetchAndSetUserData({ force: true });
      } catch (error) {
        // Log but don't abort polling - network errors are expected during webhook processing
        errorReporter.expected('Failed to fetch user data during subscription sync poll', error, {
          feature: 'subscription',
          operation: 'checkoutSyncPoll',
          attemptNumber: syncRetryCountRef.current,
          sessionId: sessionId || undefined,
        });
      }

      // Stop if the effect was cleaned up while the fetch was in flight
      if (cancelled) return;

      // Get the latest subscription from the store (updated by the fetch above)
      const state = useAuthStore.getState();
      const currentSubscription = state.userData?.subscription;

      if (isSyncedToPurchase(currentSubscription)) {
        setIsSubscriptionSynced(true);
        isPollingRef.current = false;
        return;
      }

      // Retry if subscription not synced yet
      if (syncRetryCountRef.current < maxSyncRetries) {
        syncRetryCountRef.current++;
        syncTimeoutId = setTimeout(checkSubscriptionSync, 2000);
      } else {
        // Max retries reached without subscription sync - log for monitoring
        errorReporter.critical(
          'Subscription sync failed after max retries - user may not have access to paid features',
          new Error('SUBSCRIPTION_SYNC_TIMEOUT'),
          {
            feature: 'subscription',
            operation: 'checkoutSync',
            sessionId: sessionId || undefined,
            retryCount: syncRetryCountRef.current,
            lastKnownStatus: currentSubscription?.status || 'null',
            lastKnownProductId: currentSubscription?.product_id || 'null',
            purchasedProductId: purchasedProductId || 'null',
            lastKnownPriceId: currentSubscription?.price_id || 'null',
            purchasedPriceId: purchasedPriceId || 'null',
          },
        );

        // Tell the user plainly that the upgrade did not land. Showing a success
        // screen here is what sent users back to buy the same plan again.
        setSyncFailed(true);
        showAlert(
          'Your payment went through, but we could not activate your plan. ' +
            'Please contact support instead of purchasing again, so you are not charged twice.',
          'error',
        );
        setIsSubscriptionSynced(true);
        isPollingRef.current = false;
      }
    };

    // Start checking subscription sync after a short delay
    syncTimeoutId = setTimeout(checkSubscriptionSync, 1000);

    return () => {
      cancelled = true;
      if (syncTimeoutId) clearTimeout(syncTimeoutId);
      isPollingRef.current = false;
      syncRetryCountRef.current = 0; // Reset retry count on cleanup
    };
  }, [
    isUpdatingDrip,
    isSubscriptionSynced,
    fetchAndSetUserData,
    isSyncedToPurchase,
    purchasedProductId,
    purchasedPriceId,
    sessionId,
    showAlert,
  ]);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;
    let timeoutId: NodeJS.Timeout;
    let cancelled = false;

    const verifyAndUpdateDrip = async () => {
      try {
        // If we have a session_id, verify directly with Stripe via our API
        if (sessionId) {
          const response = await noteGoApiFetch<
            VerifyCheckoutResponse,
            unknown,
            undefined,
            // eslint-disable-next-line @typescript-eslint/no-empty-object-type
            {},
            { session_id: string },
            // eslint-disable-next-line @typescript-eslint/no-empty-object-type
            {}
          >({
            url: '/subscriptions/verify-checkout-session',
            method: 'get',
            queryParams: { session_id: sessionId },
          });

          // Stop if the effect was cleaned up while the fetch was in flight
          if (cancelled) return;

          if (response.status === 'complete' && response.plan_name) {
            // Got the plan directly from Stripe - no webhook delay.
            // NOTE: this reads Stripe, not our DB. It confirms the charge, never
            // that the user doc was updated — that is what the sync poll is for.
            setDisplayPlanName(response.plan_name);
            setPurchasedProductId(response.product_id);

            // Also refresh user data to sync Firestore and wait for it
            await fetchAndSetUserData({ force: true });
            if (cancelled) return;

            // Update Drip with the plan name
            if (currentUser?.email) {
              const result = await dripService.updatePlan(currentUser.email, response.plan_name);
              if (cancelled) return;
              if (!result.success) {
                showAlert('There was an issue updating your account preferences.', 'warning');
              }
            }
            setIsUpdatingDrip(false);
            return;
          }

          // Handle setup mode (payment method update)
          if (response.mode === 'setup') {
            setDisplayPlanName('your updated payment');
            setIsSubscriptionSynced(true); // No subscription change needed for setup mode
            setIsUpdatingDrip(false);
            return;
          }

          // Session not complete yet, retry
          if (retryCount < maxRetries) {
            retryCount++;
            timeoutId = setTimeout(verifyAndUpdateDrip, 2000);
            return;
          }
        }

        // Fallback: no session_id or max retries reached
        setDisplayPlanName('your new plan');
        setIsUpdatingDrip(false);
      } catch (error) {
        // Stop if the effect was cleaned up while the request was in flight
        if (cancelled) return;

        // Log error and retry if possible
        if (retryCount < maxRetries) {
          // Debug level for retries - expected during webhook processing delays
          logger.debug('subscription', 'Verification failed, retrying', {
            attempt: retryCount + 1,
            sessionId,
          });
          retryCount++;
          timeoutId = setTimeout(verifyAndUpdateDrip, 2000);
        } else {
          // Max retries exhausted - log as critical for monitoring
          errorReporter.critical('Checkout verification failed after max retries', error, {
            feature: 'subscription',
            operation: 'verifyCheckout',
            sessionId: sessionId || undefined,
            totalAttempts: maxRetries,
          });
          setDisplayPlanName('your new plan');
          setIsUpdatingDrip(false);
        }
      }
    };

    // Start verification immediately if we have session_id, otherwise short delay
    const initialDelay = sessionId ? 500 : 3000;
    timeoutId = setTimeout(verifyAndUpdateDrip, initialDelay);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, currentUser?.email, showAlert, fetchAndSetUserData]);

  const handleContinue = () => {
    // Navigate immediately - user data has already been refreshed during the sync process
    // The accountStatus fix ensures 'incomplete' status won't block access
    navigate(syncFailed ? RouteMap.SUPPORT : RouteMap.HOME);
  };

  // Button is enabled when both checkout verification is complete AND subscription is synced
  const isReady = !isUpdatingDrip && isSubscriptionSynced;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      p={4}
      pt={8}
      sx={{ backgroundColor: '#f5f5f5' }}
    >
      <Box
        maxWidth="600px"
        width="100%"
        borderRadius="1.5rem"
        p={4}
        textAlign="center"
        sx={{
          backgroundColor: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          {syncFailed ? (
            <ErrorOutline sx={{ fontSize: 60, color: 'warning.main' }} />
          ) : (
            <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
          )}

          <Typography variant="h5" fontWeight="700" color="text.primary">
            {syncFailed ? "We couldn't activate your plan" : 'Thank You for Your Purchase!'}
          </Typography>

          {!syncFailed && (
            <Typography variant="body1" fontWeight="600" color="text.secondary">
              Welcome to {displayPlanName}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '400px' }}>
            {syncFailed
              ? 'Your payment went through, but we could not switch your account over. Please contact support so we can finish this for you — purchasing again will only charge you a second time.'
              : "Your payment has been processed successfully. You're all set and ready to go with your new plan!"}
          </Typography>

          {!isReady && (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Setting up your account...
              </Typography>
            </Stack>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            disabled={!isReady}
            sx={{ mt: 2, px: 4, py: 1.5 }}
          >
            {syncFailed ? 'Contact Support' : 'Get Started'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default CheckoutSuccess;
